import { useState, useEffect } from 'react';
import { X, Download } from '@phosphor-icons/react';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show banner for iOS after 5 seconds
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }

    // Android / Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-bottom"
        data-testid="pwa-install-banner"
      >
        <div className="max-w-lg mx-auto bg-white border border-gray-200 shadow-lg rounded-2xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-[#E63946] rounded-xl flex items-center justify-center flex-shrink-0">
            <Download size={24} weight="bold" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-['Outfit'] font-bold text-gray-900 text-sm">Installer TrouverMaPiece</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {isIOS ? 'Ajoutez l\'app sur votre ecran d\'accueil' : 'Acces rapide depuis votre ecran d\'accueil'}
            </p>
          </div>
          <button
            onClick={handleInstall}
            className="bg-[#E63946] text-white px-4 py-2 rounded-lg text-sm font-semibold flex-shrink-0 hover:bg-[#d32f3d] transition-colors"
            data-testid="pwa-install-button"
          >
            Installer
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            data-testid="pwa-dismiss-button"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* iOS guide modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mb-4 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-['Outfit'] font-bold text-gray-900">Installer sur iPhone</h3>
              <button onClick={handleDismiss} className="text-gray-400">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <p>Appuyez sur le bouton <strong>Partager</strong> (icone carre avec fleche vers le haut) en bas de Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <p>Faites defiler et appuyez sur <strong>"Sur l'ecran d'accueil"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#E63946] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <p>Appuyez sur <strong>"Ajouter"</strong> en haut a droite</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-full mt-6 bg-[#E63946] text-white py-3 rounded-lg font-semibold text-sm"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
